import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { YoolTable, StatusPill, RowActions, IconButton } from '../../components/ui/YoolTable';
import { Search, RefreshCw, UserPlus } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { UsersService } from '../../services/usersService';

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm || undefined
      };
      const data = await UsersService.getUsers(filters);
      setUsers(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al cargar los usuarios desde FileMaker',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (isAdmin) => isAdmin ? 'Administrador' : 'Usuario';

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      return;
    }

    try {
      // TODO: Implementar eliminación en FileMaker
      toast({
        title: 'Éxito',
        description: 'Usuario eliminado correctamente'
      });

      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al eliminar el usuario',
        variant: 'destructive'
      });
    }
  };

  const columns = [
    {
      key: 'full_name',
      label: 'Nombre',
      sortable: true,
      render: (r) => <div className="font-medium">{r.full_name}</div>,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
    },
    {
      key: 'is_admin',
      label: 'Rol',
      render: (r) => (
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
          background: r.is_admin ? '#DBEEFB' : '#ECECEC',
          color: r.is_admin ? '#0369A1' : '#54585A'
        }}>
          {getRoleLabel(r.is_admin)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (r) => <StatusPill estado={r.status} />,
    },
    {
      key: 'last_login',
      label: 'Último Acceso',
      render: (r) => r.last_login ? new Date(r.last_login).toLocaleDateString() : 'Nunca',
    },
    {
      key: 'created_at',
      label: 'Fecha Registro',
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A',
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (r) => (
        <RowActions>
          <IconButton icon="eye" title="Ver usuario" />
          <IconButton icon="edit" title="Editar usuario" />
          <IconButton icon="trash" title="Eliminar usuario" danger onClick={() => handleDeleteUser(r.id)} />
        </RowActions>
      ),
    },
  ];

  const filteredUsers = users.filter(user => {
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesRole = roleFilter === 'all' ||
                       (roleFilter === 'admin' && user.is_admin) ||
                       (roleFilter === 'user' && !user.is_admin);
    return matchesStatus && matchesRole;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestión de Usuarios</h1>
        <div className="flex gap-2">
          <Button onClick={fetchUsers} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button>
            <UserPlus className="h-4 w-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="inactive">Inactivo</SelectItem>
                <SelectItem value="suspended">Suspendido</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="user">Usuario</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuarios - FileMaker (Tabla: yoolusers, Layout: yoolusers_yoolusers)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando usuarios desde FileMaker...</div>
          ) : (
            <YoolTable
              columns={columns}
              rows={filteredUsers}
              rowKey="id"
              search={{ placeholder: 'Buscar por nombre o email...', keys: ['full_name', 'email'] }}
              variant="plain"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersList;
